import React from 'react';

import PropTypes from 'prop-types';
import queryString from 'query-string';
import { connect } from 'react-redux';
import {
    Redirect as RouterRedirect,
    withRouter,
} from 'react-router';

import appSettings from 'wanderu-config';
import { selectLocale } from 'wanderu-i18n';
import {
    buildAbsoluteUrl,
} from 'wanderu-utils';

function Redirect(props) {
    const {
        href = '',
        locale,
        location,
        mergeQueries = true,
        push = false,
        queries = {},
    } = props;

    function parseQueryString(qs) {
        if (typeof qs === 'object') {
            return qs;
        }

        return queryString.parse(qs);
    }

    const oldQueries = parseQueryString(location.search);
    let newQueries = parseQueryString(queries);

    if (mergeQueries) {
        newQueries = {
            ...oldQueries,
            ...newQueries,
        };
    }

    const isHomepage = href === '/';

    const removeLocale = isHomepage && locale === 'en-us';

    const url = buildAbsoluteUrl({
        allowCrossDomainProtocolRelative: false,
        href,
        language: removeLocale
            ? undefined
            : locale,
        queries: newQueries,
    });

    // The en-us homepage ("/") is served by the upstream app (nexus), not
    // ui-react. On the client a react-router redirect would keep the user
    // inside ui-react, so force a full-page navigation that the upstream proxy
    // can hand off to nexus. On the server, RouterRedirect already emits a real
    // HTTP redirect (itself a full-page navigation), so it needs no change.
    // Other locales' homepages are still served by ui-react, so they stay
    // client-side until nexus serves those locales too.
    //
    // Run the navigation in an effect (after commit), not in the render body:
    // a full-page navigation is a side effect, and firing it during render
    // would let it run on a render React may discard (StrictMode, an
    // interrupted concurrent render).
    const fullPageRedirect = removeLocale && appSettings.env.isClient;

    React.useEffect(() => {
        if (fullPageRedirect) {
            window.location.assign(url);
        }
    }, [fullPageRedirect, url]);

    if (fullPageRedirect) {
        return null;
    }

    return (
        <RouterRedirect
            push={push}
            to={url}
        />
    );
}

const queriesShape = PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
]);

Redirect.propTypes = {
    href: PropTypes.string,
    mergeQueries: PropTypes.bool,
    push: PropTypes.bool,
    queries: queriesShape,
};

function mapStateToProps(state, ownProps) {
    return {
        locale: ownProps.locale || selectLocale(state),
    };
}

const RedirectWithRouter = withRouter(Redirect);

RedirectWithRouter.propTypes = Redirect.propTypes;

export {
    Redirect,
    mapStateToProps,
    queriesShape,
};

export default connect(mapStateToProps)(RedirectWithRouter);
